import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock, BookOpen, CheckCircle, PlayCircle } from 'lucide-react';
import { useRealTimeActivity } from '@/hooks/useRealTimeActivity';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const RealTimeActivity: React.FC = () => {
  const { activities, loading } = useRealTimeActivity();
  const navigate = useNavigate();

  const handleActivityClick = (courseId: string) => {
    if (courseId) {
      navigate(`/courses/${courseId}`);
    }
  };

  const getActivityIcon = (activityType: string) => {
    switch (activityType) {
      case 'module_completed':
      case 'quiz_completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'session_start':
      case 'module_access':
      case 'content_module_access':
        return <PlayCircle className="h-4 w-4 text-blue-500" />;
      case 'course_access':
      case 'course_overview_access':
      case 'course_assigned':
        return <BookOpen className="h-4 w-4 text-primary" />;
      default:
        return <BookOpen className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getActivityDescription = (activity: any) => {
    switch (activity.activityType) {
      case 'module_completed':
        return `Completed ${activity.moduleName}`;
      case 'module_access':
      case 'content_module_access':
        return `Started learning ${activity.moduleName}`;
      case 'course_access':
        return `Accessed course: ${activity.moduleName}`;
      case 'course_overview_access':
        return `Viewed overview: ${activity.moduleName}`;
      case 'course_assigned':
        return `Assigned to course: ${activity.moduleName}`;
      case 'quiz_completed':
        return `Completed quiz in ${activity.moduleName}`;
      case 'session_start':
        return `Started learning ${activity.moduleName}`;
      case 'session_end':
        return `Finished studying ${activity.moduleName}`;
      case 'content_interaction':
        return `Engaged with ${activity.moduleName}`;
      case 'video_watch':
        return `Watched video in ${activity.moduleName}`;
      case 'reading':
        return `Read material in ${activity.moduleName}`;
      default:
        return `Accessed ${activity.moduleName}`;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg animate-pulse">
                <div className="h-4 w-4 bg-muted rounded" />
                <div className="flex-1 space-y-1">
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
                <div className="h-3 bg-muted rounded w-16" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Detect if we're in fallback mode (showing only course assignments)
  const isShowingFallback = activities.length > 0 && activities.every(a => a.activityType === 'course_assigned');

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Fallback mode banner */}
          {isShowingFallback && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 space-y-2">
              <div className="flex items-start gap-3">
                <BookOpen className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    Your Assigned Courses
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Start exploring your courses to see your learning activity tracked here in real-time
                  </p>
                  <button
                    onClick={() => navigate('/courses')}
                    className="text-xs font-medium text-primary hover:text-primary/80 hover:underline inline-flex items-center gap-1 mt-1"
                  >
                    Start Learning →
                  </button>
                </div>
              </div>
            </div>
          )}

          {activities.length === 0 ? (
            <div className="text-center py-6">
              <BookOpen className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium mb-1">No recent activity</p>
              <p className="text-xs text-muted-foreground mb-4">
                Start learning to see your activity here
              </p>
              <button
                onClick={() => navigate('/courses')}
                className="text-xs text-primary hover:underline"
              >
                Browse Courses →
              </button>
            </div>
          ) : (
            activities.map((activity) => {
              const progressPercentage = activity.metadata?.progress_percentage || 0;
              const getProgressColor = (progress: number) => {
                if (progress >= 67) return 'bg-green-500';
                if (progress >= 34) return 'bg-yellow-500';
                return 'bg-red-500';
              };

              return (
                <div 
                  key={activity.id} 
                  onClick={() => handleActivityClick(activity.courseId)}
                  className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted hover:shadow-md transition-all duration-200 hover:scale-[1.02]"
                >
                  {getActivityIcon(activity.activityType)}
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {getActivityDescription(activity)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {activity.courseName}
                      {activity.timeSpent > 0 && ` • ${Math.round(activity.timeSpent / 60)} min`}
                    </p>
                    {progressPercentage > 0 && (
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-medium">{progressPercentage}%</span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${getProgressColor(progressPercentage)} transition-all duration-300`}
                            style={{ width: `${progressPercentage}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default RealTimeActivity;